/**
 * VendorList page — searchable, sortable, filterable DataTable of vendors.
 *
 * Requirements: 1.1, 1.2, 2.4
 */

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { vendorService, type VendorListParams } from '@/services/vendorService';
import type { Vendor, SortDirection } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

const RISK_TIER_VARIANTS: Record<string, BadgeVariant> = {
  Critical: 'red',
  High: 'yellow',
  Medium: 'blue',
  Low: 'green',
};

const RISK_TIERS = ['Critical', 'High', 'Medium', 'Low'] as const;

export function VendorList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.isAdmin ||
    user?.roles.some((r) =>
      ['Admin', 'Contract Specialist', 'Facilities Admin'].includes(r),
    );

  const [params, setParams] = useState<VendorListParams>({
    page: 1,
    pageSize: 25,
    sortBy: 'vendorName',
    sortDir: 'asc',
    isActive: true,
  });

  const [riskTierFilter, setRiskTierFilter] = useState<string>('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['vendors', params, riskTierFilter],
    queryFn: () =>
      vendorService.list({
        ...params,
        riskTier: riskTierFilter || undefined,
      }),
  });

  const handleSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleSort = useCallback((key: string, direction: SortDirection) => {
    setParams((prev) => ({ ...prev, sortBy: key, sortDir: direction }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const columns: Column<Vendor & Record<string, unknown>>[] = [
    {
      key: 'vendorName',
      header: 'Vendor Name',
      sortable: true,
      render: (row) => (
        <Link
          to={`/vendors/${row.vendorId}`}
          className="text-baker-red no-underline font-medium hover:underline"
        >
          {row.vendorName}
        </Link>
      ),
    },
    {
      key: 'riskTier',
      header: 'Risk Tier',
      sortable: true,
      width: '120px',
      render: (row) => (
        <StatusBadge
          label={row.riskTier}
          variant={RISK_TIER_VARIANTS[row.riskTier] ?? 'gray'}
        />
      ),
    },
    {
      key: 'vendorCategory',
      header: 'Category',
      sortable: false,
      render: (row) => row.vendorCategory ?? '—',
    },
    {
      key: 'departmentScope',
      header: 'Department',
      sortable: true,
      width: '140px',
    },
    {
      key: 'city',
      header: 'City',
      sortable: false,
      render: (row) => row.city ?? '—',
    },
    {
      key: 'state',
      header: 'State',
      sortable: false,
      width: '80px',
      render: (row) => row.state ?? '—',
    },
    {
      key: 'updatedAt',
      header: 'Last Updated',
      sortable: true,
      width: '140px',
      render: (row) =>
        row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—',
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h1 className="m-0 text-lg font-semibold text-text-primary">Vendors</h1>
        {canWrite && (
          <button
            onClick={() => navigate('/vendors/new')}
            className="px-4 py-2 rounded-lg border-none bg-baker-red text-white cursor-pointer text-sm font-medium hover:bg-baker-red/90 transition-all"
          >
            + New Vendor
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap items-end">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Risk Tier</label>
          <select
            value={riskTierFilter}
            onChange={(e) => {
              setRiskTierFilter(e.target.value);
              setParams((prev) => ({ ...prev, page: 1 }));
            }}
            className="py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          >
            <option value="">All</option>
            {RISK_TIERS.map((tier) => (
              <option key={tier} value={tier}>{tier}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
          <select
            value={params.isActive === undefined ? '' : params.isActive ? 'active' : 'inactive'}
            onChange={(e) => {
              const val = e.target.value;
              setParams((prev) => ({
                ...prev,
                isActive: val === '' ? undefined : val === 'active',
                page: 1,
              }));
            }}
            className="py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {error ? (
        <ErrorMessage
          message={(error as Error).message ?? 'Failed to load vendors.'}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <LoadingSpinner message="Loading vendors…" />
      ) : (
        <DataTable
          columns={columns}
          data={(data?.data ?? []) as (Vendor & Record<string, unknown>)[]}
          rowKey={(row) => row.vendorId}
          totalItems={data?.total}
          page={params.page}
          pageSize={params.pageSize}
          onPageChange={handlePageChange}
          onSort={handleSort}
          onSearch={handleSearch}
          searchPlaceholder="Search vendors…"
          emptyMessage="No vendors found."
        />
      )}
    </div>
  );
}
